import React, { useEffect, useState, useCallback } from "react";
import "./DayRangeAnalysis.css";
import { environment } from "environment/environment";
import useNiftyWebsocket from "service/websocket/NiftyWebsocketApi";
import usePageTitle from "component/common/PageTitle";

export default function DayRangeAnalysis() {

    // -------------------
    // WebSocket
    // -------------------
    const { options } = useNiftyWebsocket(environment.niftyWebsocketUrl);

    // -------------------
    // State
    // -------------------
    const [indexDayRangeData, setIndexDayRangeData] = useState(null);
    const [niftyOptionDataModelList, setNiftyOptionDataModelList] = useState([]);
    const [selectedStrikeFilter, setSelectedStrikeFilter] = useState("10");
    const [indexFilterValueChanges, setIndexFilterValueChangeFilter] = useState("NSE:NIFTY");

    // -------------------
    // Page Title
    // -------------------
    usePageTitle(
        indexDayRangeData?.niftySpotPrice ?? "N/A",
        indexDayRangeData?.expiryDate ?? "N/A"
    );

    // -------------------
    // Filter Option Data (MEMOIZED)
    // -------------------
    const filterNiftyOptionData = useCallback((data, spot) => {
        if (!spot || selectedStrikeFilter === "ALL") return data;

        const range = Number(selectedStrikeFilter) - 1;

        const closestIndex = data.reduce(
            (closestIdx, curr, idx) =>
                Math.abs(curr.dayRangeStrikePrice - spot) < Math.abs(data[closestIdx].dayRangeStrikePrice - spot) ? idx : closestIdx, 0);

        const start = Math.max(0, closestIndex - range);
        const end = Math.min(data.length - 1, closestIndex + range);

        return data.slice(start, end + 2);
    }, [selectedStrikeFilter]);

    // -------------------
    // Fetch WebSocket Data
    // -------------------
    useEffect(() => {
        const niftyData = options?.[indexFilterValueChanges];
        if (!niftyData) return;

        if (niftyData.indexDayRange) {
            setIndexDayRangeData(niftyData.indexDayRange);
        }

        if (Array.isArray(niftyData.indexRangeDataModelList) && niftyData.indexRangeDataModelList.length > 0 ) {
            const optionList = niftyData.indexRangeDataModelList;

            const filtered = filterNiftyOptionData(optionList, optionList[0]?.indexOpenPrice || 0);

            setNiftyOptionDataModelList(filtered);
            console.log("Filtered Option List:", niftyOptionDataModelList);
        }
    }, [options, indexFilterValueChanges, filterNiftyOptionData]);

    // -------------------
    // Handlers
    // -------------------
    const handleStrikeFilterChange = (e) => {
        setSelectedStrikeFilter(e.target.value);
    };

    const handleIndexFilterValueChange = (e) => {
        setIndexFilterValueChangeFilter(e.target.value);
    };

    // -------------------
    // Helpers
    // -------------------
    const getCellValueClass = (value) => {
        if (value == null) return "";
        return value < 0 ? "text-danger" : "text-success";
    };

    const getZeroValueClass = (v1, v2) => {
        if (v1 == null || v2 == null) return "";
        return v1 === 0 && v2 === 0 ? "zero-value-class" : "";
    };

    // -------------------
    // Header Data
    // -------------------
    const headerData = [
        {
            label1: "Top-1",
            value1: indexDayRangeData ? Number(indexDayRangeData.dayTopOne || 0).toFixed(2) : "--",
            label2: "Top-2",
            value2: indexDayRangeData ? Number(indexDayRangeData.dayTopTwo || 0).toFixed(2) : "--",
        },
        {
            label1: "Bottom-1",
            value1: indexDayRangeData ? Number(indexDayRangeData.dayBottomOne || 0).toFixed(2) : "--",
            label2: "Bottom-2",
            value2: indexDayRangeData ? Number(indexDayRangeData.dayBottomTwo || 0).toFixed(2) : "--",
        },
        {
            label1: "OPEN",
            value1: indexDayRangeData ? Number(indexDayRangeData.niftyOpenPrice || 0).toFixed(2) : "--",
            label2: "CLOSE",
            value2: indexDayRangeData ? Number(indexDayRangeData.niftySpotPrice || 0).toFixed(2) : "--",
        },
        {
            label1: "ExpDate",
            value1: indexDayRangeData?.expiryDate || "--",
            label2: "DateTime",
            value2: indexDayRangeData?.currentDateTime || "--",
        },
    ];

    // -------------------
    // JSX
    // -------------------
    return (
        <div className="day-range-analysis">
            <header className="header">
                <nav className="nav-container">
                    <div className="left-nav header-flex">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="noptionwave logo"
                            />
                            {/* <span className="index-name">{indexSymbolName}</span> */}
                            <select name="indexFilter" className="index-filter" value={indexFilterValueChanges} onChange={handleIndexFilterValueChange} style={{ cursor: "pointer" }}>
                                <option value="NSE:NIFTY">NIFTY</option>
                                <option value="BSE:SENSEX">SENSEX</option>
                                <option value="NSE:BANKNIFTY">BANKNIFTY</option>
                                <option value="NSE:FINNIFTY">FINNIFTY</option>
                                <option value="NSE:MIDCPNIFTY">MIDCPNIFTY</option>
                            </select>
                        </div>
                        {headerData.map((item, idx) => (
                            <div className="header-item" key={idx}>
                                <span className="header-line">{item.label1} : {item.value1}</span>
                                <span className="header-line">{item.label2} : {item.value2}</span>
                            </div>
                        ))}
                    </div>
                </nav>
            </header>

            <div className="table-container">
                <table className="responsive-table table-bordered text-center sticky-header">
                    <thead>
                        <tr>
                            <th>CEDELTA-2</th>
                            <th>CEDELTA-1</th>
                            <th>DSTRIKE</th>
                            <th>DEOR</th>
                            <th>DELTA</th>
                            <th>EOR</th>
                            {/* <th>CEPC-PEOP</th>
                            <th>CEOP+PEOP</th>
                            <th>CEPC</th>
                            <th>CEOP</th> */}
                            <th>CELTP</th>
                            <th>
                                <select name="strikeFilter" className="strike-filter" value={selectedStrikeFilter} onChange={handleStrikeFilterChange} style={{ cursor: "pointer" }}>
                                    <option value="10">ATM</option>
                                    <option value="ALL">ALL</option>
                                </select>
                            </th>
                            <th>PELTP</th>
                            {/* <th>PEOP</th>
                            <th>PEPC</th>
                            <th>PEOP+CEPC</th>
                            <th>PEOP-CEOP</th> */}
                            <th>EOS</th>
                            <th>DELTA</th>
                            <th>DEOS</th>
                            <th>DSTRIKE</th>
                            <th>PEDELTA-1</th>
                            <th>PEDELTA-2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {niftyOptionDataModelList.length > 0 ? (
                            niftyOptionDataModelList.map((row, idx) => {
                                const nextRow = niftyOptionDataModelList[idx + 1];
                                const spot = options?.[indexFilterValueChanges]?.indexLiveDataModel?.open_price;

                                const isSpotBetween =
                                    nextRow &&
                                    spot != null &&
                                    spot > Math.min(row.dayRangeStrikePrice, nextRow.dayRangeStrikePrice) &&
                                    spot < Math.max(row.dayRangeStrikePrice, nextRow.dayRangeStrikePrice);
                                return (
                                    <React.Fragment key={idx}>
                                        {/* Original row */}
                                        <tr>
                                            <td className={getZeroValueClass(row.ceDeltaZeroOne, row.ceDeltaZeroTwo)}>{Number(row.ceDeltaZeroTwo).toFixed(2)??"--"}</td>
                                            <td className={getZeroValueClass(row.ceDeltaZeroOne, row.ceDeltaZeroTwo)}>{Number(row.ceDeltaZeroOne).toFixed(2)??"--"}</td>
                                            <td > --</td>
                                            <td className="DEOR-color">{Number(row.indexOpenPrice + row.cePreviousClosePeOpenPriceDiff).toFixed(2)??"--"}</td>
                                            <td >{Number(row.ceDelta).toFixed(2)??"--"}</td>
                                            <td className="EOR-color"> {Number(row.dayRangeEor).toFixed(2)??"--"}</td>
                                            {/* <td className={getCellValueClass(row.cePreviousClosePeOpenPriceDiff)}>{row.cePreviousClosePeOpenPriceDiff ?? "--"}</td>
                                            <td> {row.ceOpenPeOpenPricePlus ?? "--"}</td>
                                            <td >{row.cePreviousClosePrice ?? "--"}</td>
                                            <td >{row.ceOpenPrice ?? "--"}</td> */}
                                            <td >{row.ceLtp ?? "--"}</td>
                                            <td className="strike-price-color">{row.dayRangeStrikePrice ?? "--"} </td>
                                            <td >{row.peLtp ?? "--"}</td>
                                            {/* <td >{row.peOpenPrice ?? "--"}</td>
                                            <td>{row.pePreviousClosePrice ?? "--"}</td>
                                            <td>{row.peOpenCePreviousClosePricePlus ?? "--"} </td>
                                            <td className={getCellValueClass(row.peOpenCeOpenPriceDiff)}>{row.peOpenCeOpenPriceDiff ?? "--"}</td> */}
                                            <td className="EOR-color"> {Number(row.dayRangeEos).toFixed(2)??"--"}</td>
                                            <td >{Number(row.peDelta).toFixed(2)??"--"}</td>
                                            <td className="DEOR-color">{Number(row.indexOpenPrice + row.peOpenCeOpenPriceDiff).toFixed(2)??"--"}</td>
                                            <td > --</td>
                                            <td className={getZeroValueClass(row.peDeltaZeroOne, row.peDeltaZeroTwo)}>{Number(row.peDeltaZeroTwo).toFixed(2)??"--"}</td>
                                            <td className={getZeroValueClass(row.peDeltaZeroOne, row.peDeltaZeroTwo)}>{Number(row.peDeltaZeroOne).toFixed(2)??"--"}</td>
                                        </tr>
                                        {isSpotBetween && (
                                            <tr className="spot-line">
                                                <td colSpan="15"> {row.indexOpenPrice ?? "--"}</td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="15">Waiting for live data...</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot className="fixed-footer">
                        <tr className="footer-left">
                            <td colSpan="15">copyrights © 2026 | noptionwave </td>
                        </tr >

                    </tfoot>

                </table>
            </div>
        </div>
    );
}
